import React, { useState, useEffect, useRef } from 'react';
import { getCieCodes } from '../../servicios/apiClinica';

const CieAutocomplete = ({ value, type, cieCodesList, onUpdate, disabled, style }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = cieCodesList.filter(item => {
        const term = (value || '').toLowerCase();
        if (!term) return false;
        
        return item.code.toLowerCase().includes(term) || item.description.toLowerCase().includes(term);
    }).slice(0, 50);

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
            {type === 'description' ? (
                <textarea
                    style={{ ...style, resize: 'none', minHeight: '32px', overflow: 'hidden', padding: '2px', fontSize: '11px', lineHeight: '1.2' }}
                    rows={2}
                    value={value || ''}
                    onChange={(e) => {
                        onUpdate(null, e.target.value, false);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    disabled={disabled}
                />
            ) : (
                <input
                    type="text"
                    style={{ ...style, textAlign: 'center', fontSize: '11px', fontWeight: 'bold' }}
                    value={value || ''}
                    onChange={(e) => {
                        onUpdate(e.target.value, null, false);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    disabled={disabled}
                />
            )}
            {isOpen && !disabled && value && filteredOptions.length > 0 && (
                <ul style={{
                    position: 'absolute',
                    top: '100%',
                    left: type === 'code' ? 'auto' : 0,
                    right: type === 'code' ? 0 : 'auto',
                    minWidth: '400px',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 9999,
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    textAlign: 'left'
                }}>
                    {filteredOptions.map((item) => (
                        <li 
                            key={item.code}
                            style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '11px', fontFamily: 'Arial', lineHeight: '1.2' }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onUpdate(item.code, item.description, true);
                                setIsOpen(false);
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                            <strong style={{ color: '#002b5e' }}>{item.code}</strong> - {item.description}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const Fase1011 = ({ isViewOnly, formData, setFormData }) => {
    const [cieCodesList, setCieCodesList] = useState([]);
    useEffect(() => {
        const fetchCodes = async () => {
            const data = await getCieCodes();
            setCieCodesList(data);
        };
        fetchCodes();
    }, []);

    const updateDiagnosticPlans = (field, value) => {
        setFormData(prev => ({
            ...prev,
            diagnostic_plans: {
                ...(prev.diagnostic_plans || {}),
                [field]: value
            }
        }));
    };

    const updateDiagnosticNotes = (index, value) => {
        const currentNotes = formData.diagnostic_plans?.notes || ['', '', ''];
        const newNotes = [...currentNotes];
        newNotes[index] = value;
        setFormData(prev => ({
            ...prev,
            diagnostic_plans: {
                ...(prev.diagnostic_plans || {}),
                notes: newNotes
            }
        }));
    };

    const updateDiagnosis = (index, field, value) => {
        const currentDiagnoses = formData.diagnoses || [{}, {}, {}, {}];
        const newDiagnoses = [...currentDiagnoses];
        newDiagnoses[index] = { ...newDiagnoses[index], [field]: value };
        setFormData(prev => ({
            ...prev,
            diagnoses: newDiagnoses
        }));
    };

    const handleDiagnosisChange = (index, code, description, isFinal) => {
        setFormData(prev => {
            const current = prev.diagnoses || [{}, {}, {}, {}];
            const next = [...current];
            
            if (code !== null) next[index] = { ...next[index], cie: code };
            if (description !== null) next[index] = { ...next[index], description: description };
            
            const newState = { ...prev, diagnoses: next };

            // Auto-fill consent diagnosis if it's the primary diagnosis (index 0)
            if (index === 0) {
                newState.consent = { 
                    ...(prev.consent || {}), 
                    diagnostico: next[index].description || prev.consent?.diagnostico || '',
                    cie10: next[index].cie || prev.consent?.cie10 || ''
                };
            }
            return newState;
        });
    };

    const updateFooter = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const plans = formData.diagnostic_plans || {};
    const notes = plans.notes || ['', '', ''];
    const currentDiagnoses = formData.diagnoses || [{}, {}, {}, {}];
    const numDiagnoses = Math.max(4, currentDiagnoses.length);
    const diagnosisPairs = [];
    for (let i = 0; i < numDiagnoses; i += 2) {
        diagnosisPairs.push(i);
    }

    const HeaderStyle = { backgroundColor: '#b5d7a8', color: '#000', fontWeight: 'bold', padding: '4px 8px', borderBottom: '1px solid #000' };
    const SubHeaderStyle = { backgroundColor: '#d9ead3', color: '#000', fontSize: '9px', padding: '4px 6px', textAlign: 'center', fontWeight: 'bold', verticalAlign: 'middle', borderRight: '1px solid #000', borderBottom: '1px solid #000' };
    const YellowBox = { backgroundColor: '#fff2cc', width: '24px', textAlign: 'center', verticalAlign: 'middle', borderRight: '1px solid #000', borderBottom: '1px solid #000' };
    const InputStyle = { width: '100%', border: 'none', outline: 'none', backgroundColor: 'transparent', height: '100%', fontFamily: 'Arial, sans-serif' };
    const InputStyleBordered = { width: '100%', border: 'none', outline: 'none', backgroundColor: 'transparent', padding: '4px', fontFamily: 'Arial, sans-serif' };
    const TableStyle = { width: '100%', border: '1.5px solid #000', borderCollapse: 'collapse', marginBottom: '0', fontFamily: 'Arial, sans-serif', fontSize: '12px' };
    const TdStyle = { borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '2px' };

    return (
        <div style={{ width: '100%', marginBottom: '0' }}>
            <table style={{ ...TableStyle, marginBottom: '0', borderBottom: 'none', borderTop: 'none' }}>
                <tbody>
                    {/* FASE 10 */}
                    <tr>
                        <td colSpan="10" style={{ ...HeaderStyle, borderBottom: '1px solid #000' }}>
                            <span style={{ fontSize: '16px', marginRight: '10px' }}>10</span>
                            <span style={{ fontSize: '14px' }}>PLANES DE DIAGNÓSTICO, TERAPÉUTICO Y EDUCACIONAL</span>
                        </td>
                    </tr>
                    <tr>
                        <td style={{ ...SubHeaderStyle, width: '1%', whiteSpace: 'nowrap' }}>BIOMETRÍA</td>
                        <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px' }}>
                            <input type="checkbox" checked={plans.biometry || false} onChange={e => updateDiagnosticPlans('biometry', e.target.checked)} disabled={isViewOnly} />
                        </td>
                        <td style={{ ...SubHeaderStyle, width: '1%', whiteSpace: 'nowrap' }}>QUÍMICA<br/>SANGUÍNEA</td>
                        <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px' }}>
                            <input type="checkbox" checked={plans.blood_chemistry || false} onChange={e => updateDiagnosticPlans('blood_chemistry', e.target.checked)} disabled={isViewOnly} />
                        </td>
                        <td style={{ ...SubHeaderStyle, width: '1%', whiteSpace: 'nowrap' }}>RAYOS - X</td>
                        <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px' }}>
                            <input type="checkbox" checked={plans.x_rays || false} onChange={e => updateDiagnosticPlans('x_rays', e.target.checked)} disabled={isViewOnly} />
                        </td>
                        <td style={{ ...SubHeaderStyle, width: '1%', whiteSpace: 'nowrap' }}>OTROS</td>
                        <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px' }}>
                            <input type="checkbox" checked={plans.others || false} onChange={e => updateDiagnosticPlans('others', e.target.checked)} disabled={isViewOnly} />
                        </td>
                        <td colSpan="2" style={{ ...TdStyle, width: '100%' }}>
                            <input type="text" style={InputStyleBordered} value={plans.others_text || ''} onChange={e => updateDiagnosticPlans('others_text', e.target.value)} disabled={isViewOnly} />
                        </td>
                    </tr>
                    
                    {[0, 1, 2].map((idx) => (
                        <tr key={`note-${idx}`}>
                            <td colSpan="10" style={{ ...TdStyle, height: '24px', borderBottom: idx < 2 ? '1px solid #000' : '3px solid #666', borderRight: 'none' }}>
                                <input type="text" style={InputStyleBordered} value={notes[idx] || ''} onChange={e => updateDiagnosticNotes(idx, e.target.value)} disabled={isViewOnly} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* FASE 11 */}
            <table style={{ ...TableStyle, marginBottom: '0', borderTop: 'none', borderBottom: 'none' }}>
                <tbody>
                    <tr>
                        <td colSpan="2" style={{ ...HeaderStyle, borderRight: '1px solid #000', padding: 0 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }} className="no-border-table">
                               <tbody><tr>
                                   <td style={{ width: '70%', border: 'none', padding: '4px 8px', fontWeight: 'bold' }}>
                                       <span style={{ fontSize: '16px', marginRight: '10px' }}>11</span> <span style={{ fontSize: '14px' }}>DIAGNÓSTICO</span>
                                   </td>
                                   <td style={{ fontSize: '9px', textAlign: 'right', border: 'none', padding: '4px 8px', fontWeight: 'bold' }}>PRE= PRESUNTIVO<br/>DEF= DEFINITIVO</td>
                               </tr></tbody>
                            </table>
                        </td>
                        <td style={{...SubHeaderStyle, width: '60px'}}>CIE</td>
                        <td style={{...SubHeaderStyle, width: '20px', padding: '4px 2px'}}>PRE</td>
                        <td style={{...SubHeaderStyle, width: '20px', padding: '4px 2px', borderRight: '2px solid #000'}}>DEF</td>
                        
                        <td colSpan="2" style={{...HeaderStyle, borderRight: '1px solid #000'}}></td>
                        <td style={{...SubHeaderStyle, width: '60px'}}>CIE</td>
                        <td style={{...SubHeaderStyle, width: '20px', padding: '4px 2px'}}>PRE</td>
                        <td style={{...SubHeaderStyle, width: '20px', padding: '4px 2px', borderRight: 'none'}}>DEF</td>
                    </tr>

                    {diagnosisPairs.map((rowIdx) => (
                        <tr key={`diag-${rowIdx}`}>
                            <td style={{ ...SubHeaderStyle, width: '25px', minWidth: '25px', maxWidth: '25px', borderRight: '1px solid #000' }}>{rowIdx + 1}</td>
                            <td style={{ ...TdStyle, borderRight: '1px solid #000', width: '50%', position: 'relative', verticalAlign: 'top' }}>
                                <CieAutocomplete 
                                    value={currentDiagnoses[rowIdx]?.description} 
                                    type="description" 
                                    cieCodesList={cieCodesList} 
                                    onUpdate={(c, d, f) => handleDiagnosisChange(rowIdx, c, d, f)} 
                                    disabled={isViewOnly} 
                                    style={InputStyle} 
                                />
                            </td>
                            <td style={{ ...TdStyle, width: '60px', minWidth: '60px', maxWidth: '60px', textAlign: 'center', position: 'relative', verticalAlign: 'middle' }}>
                                <CieAutocomplete 
                                    value={currentDiagnoses[rowIdx]?.cie} 
                                    type="code" 
                                    cieCodesList={cieCodesList} 
                                    onUpdate={(c, d, f) => handleDiagnosisChange(rowIdx, c, d, f)} 
                                    disabled={isViewOnly} 
                                    style={InputStyle} 
                                />
                            </td>
                            <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px' }}>
                                <input type="checkbox" checked={currentDiagnoses[rowIdx]?.pre || false} onChange={e => updateDiagnosis(rowIdx, 'pre', e.target.checked)} disabled={isViewOnly} />
                            </td>
                            <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px', borderRight: '2px solid #000' }}>
                                <input type="checkbox" checked={currentDiagnoses[rowIdx]?.def || false} onChange={e => updateDiagnosis(rowIdx, 'def', e.target.checked)} disabled={isViewOnly} />
                            </td>

                            {rowIdx + 1 < numDiagnoses ? (
                                <>
                                    <td style={{ ...SubHeaderStyle, width: '25px', minWidth: '25px', maxWidth: '25px', borderRight: '1px solid #000' }}>{rowIdx + 2}</td>
                                    <td style={{ ...TdStyle, borderRight: '1px solid #000', width: '50%', position: 'relative', verticalAlign: 'top' }}>
                                        <CieAutocomplete 
                                            value={currentDiagnoses[rowIdx + 1]?.description} 
                                            type="description" 
                                            cieCodesList={cieCodesList} 
                                            onUpdate={(c, d, f) => handleDiagnosisChange(rowIdx + 1, c, d, f)} 
                                            disabled={isViewOnly} 
                                            style={InputStyle} 
                                        />
                                    </td>
                                    <td style={{ ...TdStyle, width: '60px', minWidth: '60px', maxWidth: '60px', textAlign: 'center', position: 'relative', verticalAlign: 'middle' }}>
                                        <CieAutocomplete 
                                            value={currentDiagnoses[rowIdx + 1]?.cie} 
                                            type="code" 
                                            cieCodesList={cieCodesList} 
                                            onUpdate={(c, d, f) => handleDiagnosisChange(rowIdx + 1, c, d, f)} 
                                            disabled={isViewOnly} 
                                            style={InputStyle} 
                                        />
                                    </td>
                                    <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px' }}>
                                        <input type="checkbox" checked={currentDiagnoses[rowIdx + 1]?.pre || false} onChange={e => updateDiagnosis(rowIdx + 1, 'pre', e.target.checked)} disabled={isViewOnly} />
                                    </td>
                                    <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px', borderRight: 'none' }}>
                                        <input type="checkbox" checked={currentDiagnoses[rowIdx + 1]?.def || false} onChange={e => updateDiagnosis(rowIdx + 1, 'def', e.target.checked)} disabled={isViewOnly} />
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td style={{ ...SubHeaderStyle, width: '25px', minWidth: '25px', maxWidth: '25px', borderRight: '1px solid #000' }}></td>
                                    <td style={{ ...TdStyle, borderRight: '1px solid #000', width: '50%', position: 'relative', verticalAlign: 'top', backgroundColor: '#f0f0f0' }}></td>
                                    <td style={{ ...TdStyle, width: '60px', minWidth: '60px', maxWidth: '60px', textAlign: 'center', position: 'relative', verticalAlign: 'middle', backgroundColor: '#f0f0f0' }}></td>
                                    <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px', backgroundColor: '#f0f0f0' }}></td>
                                    <td style={{ ...YellowBox, width: '20px', minWidth: '20px', maxWidth: '20px', borderRight: 'none', backgroundColor: '#f0f0f0' }}></td>
                                </>
                            )}
                        </tr>
                    ))}
                    {!isViewOnly && (
                        <tr className="d-print-none">
                            <td colSpan="10" style={{ textAlign: 'center', padding: '10px' }}>
                                <button type="button" onClick={() => {
                                    setFormData(prev => {
                                        const cur = prev.diagnoses || [{}, {}, {}, {}];
                                        return { ...prev, diagnoses: [...cur, {}] };
                                    });
                                }} style={{ padding: '6px 12px', fontSize: '11px', cursor: 'pointer', backgroundColor: '#e6e6e6', border: '1px solid #ccc', borderRadius: '4px', fontWeight: 'bold' }}>+ Añadir diagnóstico</button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* FOOTER TABLE */}
            <table style={{ ...TableStyle, borderTop: 'none', marginBottom: '0' }}>
                <tbody>
                    <tr style={{ backgroundColor: '#e2efd9', height: '36px' }}>
                        <td style={{ ...SubHeaderStyle, width: '60px' }}>FECHA DE<br/>APERTURA</td>
                        <td style={{ backgroundColor: '#fff', borderRight: '1px solid #000', padding: '2px', width: '10%' }}>
                            <input type="date" style={InputStyleBordered} value={formData.opening_date || ''} onChange={e => updateFooter('opening_date', e.target.value)} disabled={isViewOnly} />
                        </td>
                        <td style={{ ...SubHeaderStyle, width: '60px' }}>FECHA DE<br/>CONTROL</td>
                        <td style={{ backgroundColor: '#fff', borderRight: '1px solid #000', padding: '2px', width: '10%' }}>
                            <input type="date" style={InputStyleBordered} value={formData.control_date || ''} onChange={e => updateFooter('control_date', e.target.value)} disabled={isViewOnly} />
                        </td>
                        <td style={{ ...SubHeaderStyle, width: '70px' }}>PROFESIONAL</td>
                        <td style={{ backgroundColor: '#fff', borderRight: '1px solid #000', padding: '2px', width: '25%' }}>
                            <input type="text" style={InputStyleBordered} value={formData.professional || ''} onChange={e => updateFooter('professional', e.target.value)} disabled={isViewOnly} />
                        </td>
                        <td style={{ ...SubHeaderStyle, width: '50px' }}>CÓDIGO</td>
                        <td style={{ backgroundColor: '#fff', borderRight: '1px solid #000', padding: '2px', width: '10%' }}>
                            <input type="text" style={InputStyleBordered} value={formData.professional_code || ''} onChange={e => updateFooter('professional_code', e.target.value)} disabled={isViewOnly} />
                        </td>
                        <td style={{ ...SubHeaderStyle, width: '50px' }}>FIRMA</td>
                        <td style={{ backgroundColor: '#fff', borderRight: '1px solid #000', padding: '2px', textAlign: 'center', width: '15%' }}></td>

                        <td style={{ ...SubHeaderStyle, width: '60px', borderRight: '1px solid #000' }}>NÚMERO DE<br/>HOJA</td>
                        <td style={{ backgroundColor: '#fff', padding: '2px', borderRight: 'none', width: '10%' }}>
                            <input type="text" style={InputStyleBordered} value={formData.sheet_number || ''} onChange={e => updateFooter('sheet_number', e.target.value)} disabled={isViewOnly} />
                        </td>
                    </tr>
                </tbody>
            </table>

        </div>
    );
};

export default Fase1011;
